import { Box, Divider, Typography } from "@mui/material";
import Slider from "react-slick";
import CommonGifAnimation from "../../components/CustomGifAnimation/CommonGifAnimation";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { FC, useEffect, useRef, useState } from "react";
import "./RevampOnboardingLoader.scss";
import { useAppSelector } from "../../redux-store/CustomReduxHooks";
import { isEmpty } from "lodash";
import { marked } from "marked";
import DOMPurify from "dompurify";

interface Props {
  preLoadedGif?: any[];
}

interface NextArrowProps {
  onClick?: () => void;
}

interface PrevArrowProps {
  onClick?: () => void;
}

const NextArrow: FC<NextArrowProps> = ({ onClick }) => (
  <div className="slick-next" onClick={onClick}>
    <ArrowForwardIosIcon />
  </div>
);

const PrevArrow: FC<PrevArrowProps> = ({ onClick }) => (
  <div className="slick-prev" onClick={onClick}>
    <ArrowBackIosIcon />
  </div>
);

const SliderDot: FC = () => (
  <Box
    sx={{
      width: "6px",
      height: "6px",
      bgcolor: "#F1F3F6",
      borderRadius: "50%",
    }}
  />
);

const renderSlideDot = (_i: number) => <SliderDot />;

const RevampOnboardingLoader: FC<Props> = ({ preLoadedGif = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animations, setAnimations] = useState<{ text: string; gif: string }[]>(
    [],
  );
  const [tips, setTips] = useState<string[]>([]);
  const [sanitizedData, setSanitizedData] = useState<string[]>([]);
  const cmsOnboardingAnimation = useAppSelector(
    (state) => state.CmsSlice.cmsOnboardingAnimations,
  );

  useEffect(() => {
    if (!isEmpty(cmsOnboardingAnimation)) {
      let tempAnimation = cmsOnboardingAnimation.map((item) => {
        return {
          text: item.attributes?.subTitle || "",
          gif: item.attributes.animation?.data?.attributes?.url || "",
        };
      });

      setAnimations(tempAnimation);

      let tempTips = cmsOnboardingAnimation.find(
        (item) => !isEmpty(item.attributes.toolTipsList),
      );
      if (!isEmpty(tempTips)) {
        setTips(tempTips?.attributes.toolTipsList);
      }
    }
  }, [cmsOnboardingAnimation]);

  useEffect(() => {
    const sanitizeAllRichText = async () => {
      if (tips.length === 0) return;
      const sanitizedArray = await Promise.all(
        tips.map(async (item) => {
          const rawRichText = item || "";
          const markedHtml = await marked(rawRichText, { breaks: true });
          return DOMPurify.sanitize(markedHtml);
        }),
      );
      setSanitizedData(sanitizedArray);
    };

    sanitizeAllRichText();
  }, [tips]);

  const animationSliderSettings = {
    dots: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 10000, // 10 seconds for loading animations
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    fade: true,
  };

  const sliderRef: any = useRef(null);

  const tipsSliderSettings = {
    dots: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 5000, // 5 seconds for tips auto-scroll
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchMove: true,
    arrows: true,
    dotsClass: "slick-dots slick-thumb",
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (oldIndex: number, newIndex: number) => {
      setCurrentIndex(Math.round(newIndex));
    },
    customPaging: renderSlideDot,
  };

  return (
    <Box className="onboarding-loader-wrapper">
      <Typography
        variant="h5"
        lineHeight="120%"
        textTransform="uppercase"
        marginBottom=".5rem"
        letterSpacing="1.5px"
      >
        Verifying your details
      </Typography>
      {!isEmpty(animations) && (
        <Slider {...animationSliderSettings}>
          {animations.map((animation, index) => (
            <Box key={index} className="animation-slide">
              <Typography variant="subtitle2" marginBottom="1rem">
                {animation.text}
              </Typography>
              {!isEmpty(preLoadedGif) && (
                <CommonGifAnimation
                  animationData={preLoadedGif[index]}
                  heightCustom="13.5625rem"
                  maxWidth="12.5rem"
                />
              )}
            </Box>
          ))}
        </Slider>
      )}
      <Box>
        <Box display="flex" alignItems="center" my={2}>
          <Divider style={{ flex: 1 }} />
          <Typography variant="h6" margin="0 10px" color="#767D93">
            TIPS
          </Typography>
          <Divider style={{ flex: 1 }} />
        </Box>
        <Box width="80%" margin="auto">
          <Slider
            {...tipsSliderSettings}
            ref={sliderRef}
            className="loader-tip-wrapper"
          >
            {tips.map((tip, index) => (
              <Box key={index}>
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  width="90%"
                  textAlign="center"
                  margin="0 auto"
                  dangerouslySetInnerHTML={{
                    __html: sanitizedData[index],
                  }}
                >
                  {/* {tip} */}
                </Typography>
              </Box>
            ))}
          </Slider>
          {tips.length > 1 && (
            <div className="custom-dots">
              {Array.from({
                length: tips.length,
              }).map((_, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    sliderRef?.current?.slickGoTo(index);
                  }}
                  className={`custom-dot ${
                    index === currentIndex ? "active-dot" : ""
                  }`}
                />
              ))}
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RevampOnboardingLoader;
